<template>
  <section id="receptions">
    <main style="text-align:left;white-space:normal">
      <div class="title">
        <span>
          <icon icoClass='title-ico' selected='receptions'/>
          {{ $t('receptions') }}
          <router-link to="/reception" class="btn-blue btn-small" v-if="canAdd">
            {{ $t("add_reception") }}
          </router-link>
        </span>
        <button class="btn-green btn-small" @click="showLockdowns = true" v-if="auth.isTrw && canSeeLockdowns">
          {{ $t('lockdowns') }}
        </button>
      </div>
      <div id="filters">
        <div v-if="canSeeFilters">
          <div class="field">
            <label>{{ $t("from") }}</label>
            <input type="date" v-model="filters.booked_date_after" @change="fillPage(1)"/>
          </div>
          <div class="field">
            <label>{{ $t("to") }}</label>
            <input type="date" v-model="filters.booked_date_before" @change="fillPage(1)">
          </div>
          <div class="field">
            <label>{{ $t("delivery_note") }}</label>
            <input type="text" v-model="filters.code" @keyup.enter="fillPage(1)" @change="fillPage(1)">
          </div>
          <div class="field" v-if="auth.isTrw && canSeeOriginFilter">
            <label>{{ $t("client") }}</label>
            <search-select
              inputStyle="box-sizing: border-box"
              divStyle="text-transform: uppercase;width: 100%; box-sizing: border-box; display: inline-block"
              url="client"
              field="description"
              @data="updateClient($event); fillPage(1)"
              :source="filters.client"
              :listdata="auth.userData.clients"
            />
          </div>
          <div class="field" v-if="!auth.isTrw">
            <label>{{ $t("client") }}</label>
            <search-select
              inputStyle="box-sizing: border-box"
              divStyle="text-transform: uppercase;width: 100%; box-sizing: border-box; display: inline-block"
              url="client"
              field="description"
              @data="updateClient($event); fillPage(1)"
              :source="filters.client"
            />
          </div>
          <div class="field">
            <label>{{ $t("status") }}</label>
            <span class="custom-dropdown">
              <select v-model="filters.status" @change="fillPage(1)">
                <option value=""></option>
                <option v-for="(sta, index) in status" :key="index" :value="sta.id">
                  {{ $t(sta.description) }}
                </option>
              </select>
            </span>
          </div>
        </div>
        <date-filter
          @update="$set(filters,'booked_date_before',$event.before); $set(filters,'booked_date_after',$event.after); fillPage(1)"
          :before="filters.booked_date_before"
          :after="filters.booked_date_after"
          :options="computedOptions"
          :company="companyCode"
          @reset="filters={}; fillPage(1)"
        />
      </div>
      <load-screen :notFullScreen="true" v-show="loadingList"></load-screen>
      <table v-show="!loadingList" :class="['custom-table', { 'not-trw': !auth.isTrw }]">
        <thead>
          <th v-for="(header,index) in tableHeaders[tableOrder]" :key="index" v-if="header!='actions' || canEdit || canAdd">
            <span v-if="auth.isTrw && header==='k_notes_trw'">{{ $t(header, [$t(companyCode)]) }}</span>
            <span v-else>{{ $t(header) }}</span>
          </th>
        </thead>
        <tbody>
          <tr v-for="(reception, rIndex) in objects" :key="reception.id">
            <td v-for="(header, index) in tableHeaders[tableOrder]" :key="index" v-if="header!='actions' || canEdit || canAdd">
              <div v-if="header==='booked_date'">
                <span v-if="auth.isTrw">{{ reception.booked_date | moment('D/M/YYYY') }}</span>
                <span v-else>{{ reception.booked_date | moment('D/M/YYYY - HH:mm') }}</span>
              </div>
              <div v-if="header==='booked_time'">
                {{ reception.booked_date | moment('HH:mm') }}
              </div>
              <div v-if="header==='discharge_date'">
                <span v-if="auth.isTrw">{{ reception.action_date | moment("D/M/YYYY") }}</span>
                <span v-else>{{ reception.action_date | moment('D/M/YYYY - HH:mm') }}</span>
              </div>
              <div v-if="header==='discharge_time'">
                {{ reception.action_date | moment('HH:mm') }}
              </div>
              <div v-if="header==='delivery_note'">
                {{ reception.code }}
              </div>
              <div v-if="header==='client'">
                {{ reception.client ? reception.client.description : '' }}
              </div>
              <div v-if="header==='origin'">
                {{ reception.destination }}
              </div>
              <div v-if="header==='c_notes' || header==='c_notes_trw'">
                {{ reception.client_notes }}
              </div>
              <div v-if="header==='k_notes' || header==='k_notes_trw'">
                {{ reception.company_notes }}
              </div>
              <div v-if="header==='shared_id'">
                {{ reception.shared_id }}
              </div>
              <div v-if="header==='road'">
                {{ reception.road_number }}
              </div>
              <div v-if="header==='packages'">
                {{ reception.packages }}
              </div>
              <div v-if="header==='truck'">
                <router-link v-if="reception.shipper && canEditTruck" :to="'/truck/' + reception.shipper.id">
                  {{ reception.shipper.name }}
                </router-link>
                <span v-else-if="reception.shipper">
                  {{ reception.shipper.name }}
                </span>
              </div>
              <div v-if="header === 'arrival_date'">
                <span v-if="auth.isTrw">{{ reception.arrival_date | moment("D/M/YYYY") }}</span>
                <span v-else>{{ reception.arrival_date | moment('D/M/YYYY - HH:mm') }}</span>
              </div>
              <div v-if="header==='arrival_time'">
                <span v-if="auth.isTrw">{{ reception.arrival_date | moment("HH:mm") }}</span>
              </div>
              <div v-if="header==='status'" style="position: relative">
                <status-combo
                  :line="reception"
                  :status="status"
                  :company="companyCode"
                  type="reception"
                  @statusChanged="updateLineStatus(rIndex, $event.status)"
                />
              </div>
              <div v-if="header==='reason'" style="position: relative">
                <reason-combo
                  :line="reception"
                  :reasons="reasons"
                  type="reception"
                  @reasonChanged="updateLineReason(rIndex, $event.reason)"
                />
              </div>
              <div v-if="header==='urgent'">
                <icon v-if="reception.urgent" icoClass='table-ico-orange' selected='criticalLines'/>
              </div>
              <div v-if="header==='actions'" style="padding:0; font-size:1.5em">
                <a :title="$t('attachments')" @click="openAttachments(reception)">
                  <misc-icon icoClass="title-ico" selected="attachments" :title="$t('attachments')"/>
                </a>
                <router-link :to="'/reception/' + reception.id" :title="$t('detail')" v-if="canEdit">
                  <misc-icon icoClass='title-ico' selected='edit'/>
                </router-link>
                <a :title="$t('send_sms')" :class="{disable: locked}" v-if="reception.shipper && reception.shipper.phone && auth.hasSms" @click="line=reception; showMessage=true;">
                  <misc-icon icoClass='title-ico' selected='sms'/>
                </a>
                <a :title="$t('delete')"  v-if="canDelete" @click="line=reception; showDelete=true">
                  <misc-icon icoClass='title-ico' selected='delete' :title="$t('delete')"/>
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div id="pages">
        <div class="move-page" @click="page > 1 ? fillPage(page-1) : ''">
          «{{ $t('prev') }}.
        </div>
        <div v-for="pageShown in pagesShown" :key="pageShown" :class="page===pageShown? 'selected-page': 'page'" @click="fillPage(pageShown)">
          {{ pageShown }}
        </div>
        <div class="move-page" @click="page < count/pageSize ? fillPage(page+1) : ''">
          {{ $t('next') }}.»
        </div>
      </div>
      <modal v-show="showDelete">
        <div slot="body" class="delete-modal">{{ $t('delete_message') }}
          <div class="buttons">
            <button class="btn-green" @click="deleteLine(line)">{{ $t('ok') }}</button>
            <button class="btn-black" @click="showDelete=false; line=undefined">{{ $t('cancel') }}</button>
          </div>
        </div>
      </modal>
      <modal v-show="showMessage">
        <div slot="body" class="message-modal">{{ $t('msg_to_send') }}:
          <textarea v-model="message" cols="30" rows="4" />
          <div class="buttons">
            <button class="btn-green" @click="sendingSms({id: line.id, basePath: 'reception'})">{{ $t('ok') }}</button>
            <button class="btn-black" @click="showMessage=false;">{{ $t('cancel') }}</button>
          </div>
        </div>
      </modal>
      <lockdowns v-if="showLockdowns" @close="showLockdowns = false"/>
      <modal v-if="showAttachments">
        <div slot="body">
          <attachments :title="object.date" :object="object" :canEdit="canEdit" @close="showAttachments=false" />
        </div>
      </modal>
    </main>
  </section>
</template>

<script>
import PaginatedFilteredListMixin from '@/pages/mixins/PaginatedFilteredListMixin'
import SearchSelect from './../components/SearchSelect'
import StatusCombo from './../components/StatusCombo'
import ReasonCombo from './../components/ReasonCombo'
import Attachments from './../components/Attachments'
import DateFilter from './../components/DateFilter'
import LoadScreen from './../components/LoadScreen'
import Lockdowns from '../components/Lockdowns'
import {mapActions, mapState} from 'vuex'
import Modal from './../components/Modal'
import miscIcon from './../svg/misc'
import icon from './../svg/tabs'

export default {
  name: 'receptions',
  components: {
    SearchSelect,
    StatusCombo,
    ReasonCombo,
    Attachments,
    DateFilter,
    LoadScreen,
    Lockdowns,
    miscIcon,
    Modal,
    icon
  },
  props: {},
  mixins: [PaginatedFilteredListMixin],
  created () {
    this.$emit('loading')
  },
  async mounted () {
    await this.getReceptionsData()
    this.fillPage(this.page)
    this.refreshTable()
  },
  beforeDestroy () {
    clearInterval(this.timer)
  },
  data () {
    return {
      tableHeaders: {
        defaultTrw: ['booked_date', 'booked_time', 'discharge_date', 'discharge_time', 'client', 'c_notes_trw', 'k_notes_trw', 'shared_id', 'packages', 'truck', 'arrival_date', 'arrival_time', 'status', 'reason', 'urgent', 'actions'],
        mexTrw: ['booked_date', 'booked_time', 'discharge_date', 'discharge_time', 'client', 'c_notes_trw', 'k_notes_trw', 'shared_id', 'packages', 'truck', 'arrival_date', 'arrival_time', 'status', 'reason', 'urgent', 'actions'],
        forkLiftTrw: ['booked_date', 'booked_time', 'discharge_date', 'discharge_time', 'client', 'c_notes_trw', 'k_notes_trw', 'shared_id', 'packages', 'truck', 'arrival_date', 'arrival_time', 'status', 'reason', 'urgent', 'actions'],
        default: ['booked_date', 'discharge_date', 'delivery_note', 'client', 'origin', 'c_notes', 'k_notes', 'road', 'packages', 'truck', 'arrival_date', 'status', 'reason', 'actions'],
        mex: ['booked_date', 'discharge_date', 'delivery_note', 'origin', 'k_notes', 'road', 'client', 'packages', 'truck', 'arrival_date', 'status', 'reason', 'actions'],
        forkLift: ['actions', 'booked_date', 'delivery_note', 'client', 'status', 'reason', 'k_notes', 'discharge_date', 'origin', 'road', 'packages', 'truck', 'arrival_date'],
        kiosk: ['booked_date', 'booked_time', 'discharge_date', 'discharge_time', 'client', 'c_notes_trw', 'k_notes_trw', 'shared_id', 'packages', 'truck', 'arrival_date', 'arrival_time', 'status', 'reason', 'urgent', 'actions'],
        bw: ['booked_date', 'booked_time', 'discharge_date', 'discharge_time', 'client', 'c_notes_trw', 'k_notes_trw', 'shared_id', 'packages', 'truck', 'arrival_date', 'arrival_time', 'status', 'urgent', 'actions']
      },

      companyCode: process.env.COMPANY,

      clientsMap: new Map(),
      statusMap: new Map(),

      canSeeOriginFilter: false,
      canSeeLockdowns: false,
      showAttachments: false,
      showLockdowns: false,
      canSeeFilters: false,
      canEditTruck: false,
      showMessage: false,
      showDelete: false,
      canDelete: false,
      canEdit: false,
      canAdd: false,
      locked: false,

      objects: [],
      reasons: [],
      clients: [],
      filters: {}, // IMPORTANTE: Inicializado vacío
      status: [],

      refresh: undefined,
      timer: undefined,
      line: undefined,

      tableOrder: 'default',
      pageSize: 10,
      message: '',
      date: ''
    }
  },
  computed: {
    ...mapState({
      auth: state => state.auth,

      count: state => state.receptions.count
    }),
    pagesShown: {
      get: function () {
        var pages = [this.page]
        for (let index = 1; index <= 2; index++) {
          if (this.page - index > 0) pages.push(this.page - index)
          if (this.page + index <= Math.ceil(this.count / this.pageSize)) pages.push(this.page + index)
        }
        return pages.sort(function (a, b) { return a - b })
      }
    },
    computedOptions () {
      const options = ['yesterday', 'today', 'tomorrow']
      if (this.auth.isTrw) options.push('next_week')
      return options
    }
  },
  watch: { },
  methods: {
    ...mapActions({
      checkPermissions: 'auth/checkPermissions',

      getObjects: 'receptions/list',
      deleteLines: 'receptions/delete',
      patchLine: 'receptions/patch',
      sendSms: 'receptions/sendSms',

      getStatus: 'status/list',
      getReasons: 'status/reasons',
      getClients: 'clients/list'
    }),
    deleteLine (line) {
      this.deleteLines(line.id).then(() => {
        this.fillPage(this.page)
        this.showDelete = false
      })
    },
    fillPage (page) {
      this.$set(this.filters, 'page_size', this.pageSize)

      if (!this.filters.client) {
        this.$set(this.filters, 'client', this.auth.userData.clients)
      }

      this.getPage(page)
    },
    updateClient (value) {
      this.filters.client = value
    },
    sendingSms (data) {
      if (data.id && this.locked === false) {
        this.locked = true
        data.message = this.message
        this.sendSms(data).then(() => {
          this.$notify({ group: 'global', type: 'success', duration: 4000, title: 'Success', text: this.$t('message_send') })
        })
        this.showMessage = false
      }
      setTimeout(() => (this.locked = false), 10000)
    },
    updateLineStatus (index, newStatus) {
      const data = { status: newStatus.id }
      this.applyPatchLine(index, data)
    },
    updateLineReason (index, newReason) {
      const data = { reason: newReason.code }
      this.applyPatchLine(index, data)
    },
    applyPatchLine (index, data) {
      const prev = this.objects[index]
      const local = Object.assign({}, prev, data)

      // Update the status of the line
      this.patchLine({ id: local.id, ...data })
        .then((response) => {
          // update the local object with the response
          const merged = Object.assign({}, local, response || {})
          this.$set(this.objects, index, merged)
        })
        .catch((e) => {
          // revert to the previous state in case of error
          this.$set(this.objects, index, prev)
          console.error('Error updating line:', e)
          this.$notify({ group: 'global', type: 'error', duration: 4000, title: 'Error', text: this.$t('error_saving') })
        })
    },
    refreshTable () {
      this.$nextTick(() => {
        this.refresh = setInterval(() => {
          // eslint-disable-next-line
          if (this.$route.name === 'receptions') this.fillPage(this.page)
          else clearInterval(this.refresh)
        }, (60000))// one minute
      })
      this.$nextTick(() => {
        var laps = 0
        this.timer = setInterval(() => {
          laps++
          // eslint-disable-next-line
          if (this.$route.name != 'receptions') clearInterval(this.timer)
          else {
            if (this.today) {
              if (this.today.getDate() !== (new Date()).getDate()) {
                this.today = new Date()
              }
            }

            if (this.auth.userData.roles && this.auth.userData.roles.map((x) => { return x.id }).indexOf(this.auth.kiosk.id) > -1) {
              if (this.page + 1 <= Math.ceil(this.count / this.pageSize)) this.fillPage(++this.page)
              else this.$emit('next')
            } else if (this.auth.userData.roles && this.auth.userData.roles.map((x) => { return x.id }).indexOf(this.auth.kioskReceptions.id) > -1) {
              if (this.page + 1 <= Math.ceil(this.count / this.pageSize)) this.fillPage(++this.page)
              else this.fillPage(1)
            }

            if (laps === 100) {
              laps = 0
              location.reload()
            }
          }
        }, (process.env.REFRESH_TIME * 1000))
      })
    },
    getTableOrder (permissionResponse) {
      if (this.auth.isTrw) {
        if (this.companyCode === 'bw') return 'bw'
        else if (this.auth.user === 'mexico') return 'mexTrw'
        else if (!permissionResponse.isForkLift) return 'forkLiftTrw'
        else if (!permissionResponse.isKiosk) return 'kiosk'
        else return 'defaultTrw'
      } else {
        if (this.auth.user === 'mexico') return 'mex'
        else if (!permissionResponse.isForkLift) return 'forkLift'
        else return 'default'
      }
    },
    openAttachments (reception) {
      this.object = JSON.parse(JSON.stringify(reception))
      this.showAttachments = true
    },

    // Async methods
    async getPermissions () {
      const permissionResponse = await this.checkPermissions(['addRecep', 'deleteRecep', 'editRecep', 'lockdowns', 'seeFilters', 'seeOriginFilter', 'isForkLift', 'editTruck', 'isKiosk'])
      this.canAdd = permissionResponse.addRecep
      this.canDelete = permissionResponse.deleteRecep
      this.canEdit = permissionResponse.editRecep
      this.canSeeLockdowns = permissionResponse.lockdowns
      this.canSeeFilters = permissionResponse.seeFilters
      this.canSeeOriginFilter = permissionResponse.seeOriginFilter
      this.canEditTruck = permissionResponse.editTruck
      this.tableOrder = this.getTableOrder(permissionResponse)
    },
    async getInitialData () {
      this.reasons = await this.getReasons()
      this.status = await this.getStatus('reception')
      this.statusMap = new Map(this.status.map((x) => [x.id, x]))
    },
    async getReceptionsData () {
      try {
        await this.getPermissions()
        await this.getInitialData()
      } catch (error) {
        console.error('Error getting receptions:', error)
      } finally {
        this.$emit('loaded')
      }
    }
  }
}
</script>

<style scoped lang="scss">
#receptions {
  display: table;
}
.message-modal {
  display: grid;
}
textarea {
  margin-top: 20px;
  margin-bottom: 20px;
}
.title {
  display: flex;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/pages/PageReceptions.vue